import type { SceneObject } from "./SceneHost";
import { frameHitBounds, isFrameObject } from "./frame/frameContext";
import { objectBounds } from "./selectionBounds";
import { compareZIndex, zIndexKey } from "./zIndex";

const CELL_SIZE = 256;
/** Below this count, linear scan is cheaper than building the grid. */
export const SPATIAL_GRID_THRESHOLD = 80;

function cellKey(cx: number, cy: number): string {
  return `${cx},${cy}`;
}

export class SceneSpatialIndex {
  private readonly cellSize = CELL_SIZE;
  private readonly idToIndex = new Map<string, number>();
  private cells = new Map<string, SceneObject[]>();
  private useGrid = false;

  build(objects: SceneObject[]): void {
    this.cells.clear();
    this.idToIndex.clear();
    this.useGrid = objects.length >= SPATIAL_GRID_THRESHOLD;
    if (!this.useGrid) return;

    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i]!;
      this.idToIndex.set(obj.id, i);
      const b = isFrameObject(obj) ? frameHitBounds(obj) : objectBounds(obj);
      const x0 = Math.floor(b.x / this.cellSize);
      const y0 = Math.floor(b.y / this.cellSize);
      const x1 = Math.floor((b.x + Math.max(b.w, 1)) / this.cellSize);
      const y1 = Math.floor((b.y + Math.max(b.h, 1)) / this.cellSize);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cy = y0; cy <= y1; cy++) {
          const key = cellKey(cx, cy);
          let list = this.cells.get(key);
          if (!list) {
            list = [];
            this.cells.set(key, list);
          }
          list.push(obj);
        }
      }
    }
  }

  /** Objects that may intersect the query circle, highest z-index first. */
  query(x: number, y: number, radius: number, allObjects: SceneObject[]): SceneObject[] {
    if (!this.useGrid) return allObjects;

    const x0 = Math.floor((x - radius) / this.cellSize);
    const y0 = Math.floor((y - radius) / this.cellSize);
    const x1 = Math.floor((x + radius) / this.cellSize);
    const y1 = Math.floor((y + radius) / this.cellSize);

    const seen = new Set<string>();
    const result: SceneObject[] = [];
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const list = this.cells.get(cellKey(cx, cy));
        if (!list) continue;
        for (const obj of list) {
          if (seen.has(obj.id)) continue;
          seen.add(obj.id);
          result.push(obj);
        }
      }
    }

    result.sort((a, b) => {
      const z = compareZIndex(zIndexKey(a), zIndexKey(b));
      if (z !== 0) return -z; // выше z — раньше в списке
      return (this.idToIndex.get(b.id) ?? 0) - (this.idToIndex.get(a.id) ?? 0);
    });
    return result;
  }
}

let spatialSceneStamp = 0;
let cachedSpatialStamp = -1;
let cachedSpatialLen = -1;
let cachedSpatialIndex: SceneSpatialIndex | null = null;

/** Вызывается при любом изменении сцены — переиспользуем индекс в рамках одного кадра жеста. */
export function bumpSpatialSceneStamp(): void {
  spatialSceneStamp += 1;
  cachedSpatialIndex = null;
  cachedSpatialStamp = -1;
  cachedSpatialLen = -1;
}

function spatialIndexForObjects(objects: SceneObject[]): SceneSpatialIndex {
  const len = objects.length;
  if (
    cachedSpatialIndex &&
    cachedSpatialStamp === spatialSceneStamp &&
    cachedSpatialLen === len
  ) {
    return cachedSpatialIndex;
  }
  const index = new SceneSpatialIndex();
  index.build(objects);
  cachedSpatialIndex = index;
  cachedSpatialStamp = spatialSceneStamp;
  cachedSpatialLen = len;
  return index;
}

export function spatialCandidates(
  objects: SceneObject[],
  x: number,
  y: number,
  radius: number
): SceneObject[] {
  return spatialIndexForObjects(objects).query(x, y, radius, objects);
}
